/**
 * Tipos e chamadas da gestão de usuários.
 *
 * O contrato espelha `api/internal/modulos/conta/usuarios.go`. Guardado por
 * `users.manage` no servidor — o front só esconde o item de navegação e a
 * ação por conveniência (CONSTITUICAO.md §4.1); quem decide de verdade é o
 * porteiro.
 *
 * Sem uuid (CONSTITUICAO.md §2): o e-mail é a identidade de negócio do
 * usuário, e é ele que a rota de troca de papel usa no lugar de um id
 * técnico — do mesmo jeito que cliente e produto usam `codigo`.
 */
import { api } from './api'

export type StatusUsuario = 'ativo' | 'convidado' | 'bloqueado'

/**
 * Os SEIS papéis da spec 04.
 *
 * `contexto-sessao.ts` declara só os quatro do usuário LOGADO em produção até
 * hoje; aqui o seletor de convite precisa dos seis, porque qualquer um deles
 * pode ser oferecido a quem está entrando.
 */
export type PapelCompleto =
  | 'administrador'
  | 'gerente'
  | 'caixa'
  | 'vendedor'
  | 'estoquista'
  | 'contador'

export type LinhaUsuario = {
  /** Vazio para convite ainda não aceito — ninguém digitou um nome ainda. */
  nome: string
  email: string
  papel: PapelCompleto
  papel_rotulo: string
  status: StatusUsuario
  status_rotulo: string
  ultimo_acesso_em?: string
  criado_em: string
  /** Comissão em pontos-base (§1 da constituição — nunca decimal). */
  comissao_pontos: number
  /** Vazios quando a pessoa usa só a matriz do papel-base. Quando
   *  preenchidos, é o papel personalizado da loja que SOBREPÕE `papel` na
   *  hora de autorizar — e é o nome dele que `papel_rotulo` já mostra. */
  papel_personalizado_id?: string
  papel_personalizado_nome?: string
}

export type ListaDeUsuarios = {
  usuarios: LinhaUsuario[]
  total: number
}

export type PedidoConvite = {
  email: string
  papel: PapelCompleto
  /** "Perfil de permissões — opcional": sobrepõe a matriz do papel acima. */
  papel_personalizado_id?: string
}

export type ResultadoConvite = {
  email: string
  papel: PapelCompleto
  papel_rotulo: string
  papel_personalizado_id?: string
  papel_personalizado_nome?: string
  mensagem: string
}

/**
 * O corpo do PUT. Os campos são opcionais: manda-se só o que mudou.
 *
 * `status` só aceita 'ativo' ou 'bloqueado' por aqui — 'convidado' não é um
 * estado que se atribui, é o que a pessoa É até aceitar o convite.
 *
 * `papel_personalizado_id` distingue três casos: omitido não mexe na
 * sobreposição; `''` (string vazia, explícita) REMOVE a sobreposição, e volta
 * a usar só a matriz do papel-base; qualquer outro texto TROCA para aquele
 * papel personalizado.
 */
export type PedidoAtualizarUsuario = {
  papel?: PapelCompleto
  status?: 'ativo' | 'bloqueado'
  papel_personalizado_id?: string
}

export function carregarUsuarios(sinal?: AbortSignal): Promise<ListaDeUsuarios> {
  return api.get<ListaDeUsuarios>('/usuarios', sinal)
}

export function convidarUsuario(pedido: PedidoConvite): Promise<ResultadoConvite> {
  return api.post<ResultadoConvite>('/usuarios/convites', pedido)
}

export function atualizarUsuario(
  email: string,
  pedido: PedidoAtualizarUsuario,
): Promise<LinhaUsuario> {
  return api.put<LinhaUsuario>(`/usuarios/${encodeURIComponent(email)}`, pedido)
}

/** As opções do seletor de papel no convite — mesma ordem de `porteiro.PapeisValidos`. */
export const PAPEIS_CONVITE: { chave: PapelCompleto; rotulo: string }[] = [
  { chave: 'administrador', rotulo: 'Administrador' },
  { chave: 'gerente', rotulo: 'Gerente' },
  { chave: 'caixa', rotulo: 'Caixa' },
  { chave: 'vendedor', rotulo: 'Vendedor' },
  { chave: 'estoquista', rotulo: 'Estoquista' },
  { chave: 'contador', rotulo: 'Contador' },
]
